import { API, graphqlOperation } from 'aws-amplify';
import { getUserData } from 'Services/API';
import {getSchoolYear} from "Components/Reports/util/validation";
import { graphQLRequestIAM } from './graphQLService';

const SRSurl = process.env.STUDENT_RECORD_STORE_URL;

const GET_SCREENING_WINDOW_LICENSES = `
query GetAmiraLicenses($districtId: Int!, $schoolId: Int!){
  getAmiraLicenses(districtId: $districtId, schoolId: $schoolId) {
    screeningStartDate
    screeningEndDate
  }
}`;

const UPDATE_SCREENING_WINDOW_LICENSES = `
mutation UpdateAmiraLicenses($input: UpdateAmiraLicensesInput!) {
  updateAmiraLicenses(input: $input){
    districtId
    schoolId
    screeningStartDate
    screeningEndDate
  }
}`;

export async function getScreeningWindowLicenses(districtId, schoolId) {
  const response = await graphQLRequestIAM(SRSurl, GET_SCREENING_WINDOW_LICENSES, {
    districtId: districtId,
    schoolId: schoolId,
  });

  const { screeningStartDate, screeningEndDate } = response.data.getAmiraLicenses || {};
  return {
    screeningStartDate: screeningStartDate ? new Date(screeningStartDate) : null,
    screeningEndDate: screeningEndDate ? new Date(screeningEndDate) : null,
  }
}

export async function updateScreeningWindowLicenses(startDate, endDate, districtId, schoolIds) {
  let responses = [];
  schoolIds.forEach(async (schoolId) => {
    responses.push(await graphQLRequestIAM(SRSurl, UPDATE_SCREENING_WINDOW_LICENSES, {
      input: {
        screeningStartDate: startDate,
        screeningEndDate: endDate,
        districtId: districtId,
        schoolId: schoolId,
      }
    }));
  });

  return responses;
}

//This method will return true if an activity is older than 3 months.
function shouldIgnore(activityDate){
  let today = new Date()
  let ageInMonths = (today.getFullYear()*12 + today.getMonth()) - (activityDate.getFullYear()*12 + activityDate.getMonth());
  return ageInMonths > 3 ? true : false;
}

export function inCurrentScreeningWindow(date) {
  if(shouldIgnore(date)){
    return false;
  }
  return (getScreeningWindow(date) === getScreeningWindow());
}

export function isBenchmarkWindowSet(){
  const { screeningStartDate, screeningEndDate } = getUserData();
  if (!screeningStartDate || !screeningEndDate) {
    return false;
  }

  return true;
}

export function couldGetBenchmark(){
  //A student could get a benchmark if there is no window set or if we're in the set window.
  return(!isBenchmarkWindowSet() || inBenchmarkScreeningWindow());
}

export function inBenchmarkScreeningWindow(date = new Date()) {
  const { screeningStartDate, screeningEndDate } = getUserData();
  if(!isBenchmarkWindowSet()) return false;
  return date >= new Date(screeningStartDate) && date <= new Date(screeningEndDate);
}

export function getScreeningWindow(date = new Date()){
  const thisMonth = date.getMonth() + 1;
  const schoolYearInfo = getSchoolYear();
  const customerSetting = schoolYearInfo.startDate;

  let override = false;
  if(customerSetting){
    const customerDate = new Date(customerSetting);
    if(customerDate.getMonth() + 1 === 12){
      override = true;
    }
  }
  //Preserve current logic if no setting or if we're in MOY (which simplified the logic below)
  if(!customerSetting || ((thisMonth === 12 && !override) || (thisMonth < 8))){
    if((thisMonth >= 8) && (thisMonth <12)){
      return "BOY";
    }else{
      if((thisMonth === 12) || (thisMonth < 4)){
        return "MOY";
      }else{
        return "EOY";
      }
    }
  }else{
    const syStartDate = new Date(customerSetting);
    //At this point, we're just trying to figure out if it's BOY or EOY (since MOY is handled above)
    return(date >= syStartDate) ? "BOY" : "EOY";
  }
}

export function getScreeningWindowsForSchoolYear() {
  const { startDate, endDate } = getSchoolYear();
  const startYear = startDate.getFullYear();
  const newYear = endDate.getFullYear();
  //set screening window dates to current school year
  let windows = [{
    id: "BOY",
    season: "Fall",
    startDate: startDate, //school year start date
    endDate: new Date(`${startYear}-11-30T23:59:59`),
  },{
    id: "MOY",
    season: "Winter",
    startDate: new Date(`${startYear}-12-01T00:00:00`),
    endDate: new Date(`${newYear}-03-31T23:59:59`),
  },{
    id: "EOY",
    season: "Spring",
    startDate: new Date(`${newYear}-04-01T00:00:00`),
    endDate: endDate, //school year end date
  }];

  const currentWindow = windows.find(window => window.id === getScreeningWindow());
  return windows.map(window => {

    const endDateTime = window.endDate.getTime()
    const currentWindowStartDateTime = currentWindow.startDate.getTime()
    return {
      isCurrent: window.id === currentWindow.id,
      isPast: endDateTime < currentWindowStartDateTime,
      ...window,
    }
  });
}

export function getCurrentScreeningWindowForSchoolYear() {
  const screeningWindows = getScreeningWindowsForSchoolYear();
  return screeningWindows.find(window => window.isCurrent);
}

/**
 * gets screening window details including startDate and endDate
 * @param {string} screeningWindow BOY, MOY, EOY
 * @returns window details
 */
export function getScreeningWindowDetails(screeningWindow) {
  return getScreeningWindowsForSchoolYear().find(window => window.id === screeningWindow);
}

export default {
  getScreeningWindowLicenses,
  updateScreeningWindowLicenses,
  isBenchmarkWindowSet,
  inCurrentScreeningWindow,
  inBenchmarkScreeningWindow,
  getScreeningWindow,
  getScreeningWindowDetails,
  getScreeningWindowsForSchoolYear,
}
